import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const TooltipContainer = styled(motion.div)`
  position: absolute;
  background: white;
  padding: 20px;
  border-radius: 4px;
  color: black;
  width: 400px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (max-width: 768px) {
    width: 85%!important;
    left: 50%!important;
    transform: translateX(-50%)!important;
    top: 70px!important;
  }
`;


const Title = styled.h2`
  font-family: 'Courier New', monospace;
  text-transform: uppercase;
  font-size: 16px;
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 2px solid black;
  letter-spacing: 1px;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 20px;
  overflow-y: auto;
  max-height: 400px;
`;

const TextContent = styled.div`
  flex: 1;
  font-size: 14px;
  line-height: 1.4;
  font-family: 'Courier New', monospace;
`;

const ImageContainer = styled.div`
  width: 150px;
  height: 150px;
  background: #f0f0f0;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ReadButton = styled.button`
  border: 2px solid black;
  background: none;
  padding: 8px 20px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  cursor: pointer;
  align-self: flex-start;
  transition: all 0.2s ease;

  &:hover {
    background: black;
    color: white;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: black;
  cursor: pointer;
  font-size: 20px;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;

export const ArticlePopover = ({ article, onClose }) => {
  const tooltipRef = React.useRef(null);
  const [position, setPosition] = React.useState({ left: '0', top: '0', transform: 'none' });
  const navigate = useNavigate();

  React.useEffect(() => {
    const calculatePosition = () => {
      if (!tooltipRef.current) return;
    
      const offset = 20;
      const padding = 20; // Safe area around edges
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
    
      const tooltipHeight = tooltipRef.current.offsetHeight;
      const tooltipWidth = tooltipRef.current.offsetWidth;
    
      // Trigger element position in pixels
      const starX = (article.position.x / 100) * viewportWidth;
      const starY = (article.position.y / 100) * viewportHeight;
    
      // Default positions
      let left = starX + offset; // Tooltip appears to the right
      let top = starY - tooltipHeight / 2; // Center vertically on trigger
      let transform = ''; // Default transform is none
    
      // Adjust for bottom overflow
      if (top + tooltipHeight > viewportHeight - padding) {
        top = viewportHeight - tooltipHeight - padding;
      }
    
      // Adjust for top overflow
      if (top < padding) {
        top = padding;
      }
    
      // Adjust for right overflow
      if (left + tooltipWidth > viewportWidth - padding) {
        left = starX - tooltipWidth - offset; // Position to the left of trigger
      }
    
      // Adjust for left overflow
      if (left < padding) {
        left = padding;
      }
    
      // Apply calculated position
      setPosition({
        left: `${left}px`,
        top: `${top}px`,
        transform,
      });
    };
    

    // Calculate initial position
    calculatePosition();

    // Recalculate on window resize
    window.addEventListener('resize', calculatePosition);
    return () => window.removeEventListener('resize', calculatePosition);
  }, [article.position.x, article.position.y]);

  const handleReadClick = () => {
    navigate(`/article/${article.id}`);
  };

  return (
    <TooltipContainer
      ref={tooltipRef}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      style={position}
    >
      <CloseButton onClick={onClose}>×</CloseButton>
      <Title>{article.name}</Title>
      <ContentWrapper>
        <TextContent>
          <div dangerouslySetInnerHTML={{ __html: article.summary }} />
        </TextContent>
        {article.image && <ImageContainer>
          {article.image && <img src={article.image} alt="" />}
        </ImageContainer>}
      </ContentWrapper>
      <ReadButton onClick={handleReadClick}>READ.</ReadButton>
    </TooltipContainer>
  );
}; 